import { useQueryClient } from '@tanstack/react-query';

import type { EventFeedbackForm } from '@mds/shared/types/event-feedback';

import FormBuilder, { LockedRow } from '@/components/events/form-builder';
import Card from '@/components/ui/card';
import Chip from '@/components/ui/chip';
import Switch from '@/components/ui/switch';
import {
  FEEDBACK_FORM_CHANGED,
  feedbackDraftOf,
  isFormLocked,
  replaceRequestOf,
} from '@/domain/feedback';
import * as feedback from '@/events/feedback.api';
import { stamp } from '@mds/shared/lib/dates';

/** What the API fixes for every feedback form; a form not yet loaded reads the same. */
const STANDARD_FIELDS: EventFeedbackForm['standardFields'] = {
  overallRating: { required: true, min: 1, max: 5 },
  comment: { required: false, maxLength: 5000 },
};

/** The two fields every feedback form asks, whatever its own questions are. */
export function FeedbackAlwaysAsked({
  standardFields = STANDARD_FIELDS,
}: {
  standardFields?: EventFeedbackForm['standardFields'];
}) {
  return (
    <>
      <LockedRow
        label="Overall, how would you rate this event?"
        detail={`Rating ${standardFields.overallRating.min}–${standardFields.overallRating.max} · required`}
      />
      <LockedRow
        label="Anything else you would like to tell us?"
        detail={`Comment · optional · up to ${standardFields.comment.maxLength.toLocaleString()} characters`}
      />
    </>
  );
}

/**
 * The event's post-event feedback form.
 *
 * Every event has one from the start: a draft that sends nothing until it is
 * switched on. Rating and comment are always asked and cannot be changed, so
 * they are drawn as locked rows; everything else is optional sections and
 * questions. The first invitation to go out freezes the form for good.
 */
export default function FeedbackBuilder({
  eventId,
  actionsSlot,
}: {
  eventId: string;
  /** The dialog footer that Save and Discard are drawn into. */
  actionsSlot: HTMLElement | null;
}) {
  const queryClient = useQueryClient();
  return (
    <FormBuilder
      queryKey={['feedback-form', eventId]}
      load={() => feedback.getForm(eventId)}
      save={(draft, version) => feedback.replaceForm(eventId, replaceRequestOf(draft, version))}
      actionsSlot={actionsSlot}
      draftOf={feedbackDraftOf}
      changedCode={FEEDBACK_FORM_CHANGED}
      locked={(form) => form.locked}
      noun="feedback form"
      savedNotice={(saved) =>
        saved.isEnabled
          ? 'Saved. Invitations go out to checked-in guests as soon as the event ends.'
          : 'Saved as a draft. Nothing is sent until invitations are switched on.'
      }
      saveProblem={(e) =>
        isFormLocked(e)
          ? 'Invitations have already gone out, so this form can no longer change.'
          : null
      }
      onSaved={() => {
        // The programme row and the event header carry the on/off state too.
        void queryClient.invalidateQueries({ queryKey: ['events'] });
        void queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      }}
      questionsHint="Optional. Questions here come straight after the rating. Group related ones under a section heading — the venue, the speaker, what to run next — and guests see them together."
      intro={({ loaded, draft, change, readOnly, mayManage }) => {
        const dirty = draft.isEnabled !== loaded.isEnabled;
        return (
          <Card
            title="After the event"
            action={
              <div className="flex flex-wrap items-center gap-2">
                {loaded.locked ? (
                  <Chip className="border border-hairline bg-raised text-ink-muted">
                    Locked · invitations sent
                  </Chip>
                ) : loaded.isEnabled ? (
                  <Chip className="border border-brand-edge/35 bg-brand-wash text-brand-strong">
                    Invitations on
                  </Chip>
                ) : (
                  <Chip className="border border-warn-edge bg-warn-wash text-warn-text">
                    Invitations off
                  </Chip>
                )}
                {loaded.updatedAt && <Chip>Saved {stamp(loaded.updatedAt)}</Chip>}
              </div>
            }
          >
            <div className="flex flex-col gap-4 text-base">
              <p className="m-0 text-ink-secondary">
                When the event ends, every guest who checked in and left an email is sent a private
                link to this form. Guests are always asked for a rating and a comment, so the form
                works as it is. Add your own questions below if you want more. Responses are
                anonymous.
              </p>
              {loaded.locked ? (
                <p className="m-0 text-ink-muted">
                  Invitations have been queued, so the questions are frozen: every guest answers
                  the same form.
                </p>
              ) : (
                <p className="m-0 text-ink-muted">
                  Once the first invitation goes out, the form is frozen so every guest answers the
                  same questions. Change it before the event ends.
                </p>
              )}
              {!loaded.locked && !mayManage && (
                <p className="m-0 text-ink-muted">You can read this form but not change it.</p>
              )}

              <div className="flex flex-col gap-1 rounded-xl border border-hairline bg-raised px-4 py-3">
                <Switch
                  checked={draft.isEnabled}
                  disabled={readOnly}
                  label="Send invitations when the event ends"
                  onChange={(isEnabled) => change({ ...draft, isEnabled })}
                />
                <span className="text-sm text-ink-muted">
                  {draft.isEnabled
                    ? dirty
                      ? 'Save to switch sending on.'
                      : 'Guests who check in will be invited.'
                    : dirty
                      ? 'Save to switch sending off.'
                      : 'Nothing is sent while this is off.'}
                </span>
              </div>
            </div>
          </Card>
        );
      }}
      alwaysAsked={(loaded) => <FeedbackAlwaysAsked standardFields={loaded.standardFields} />}
    />
  );
}
